import { useEffect, useRef, useState } from 'react';
import { SigningAvatar, describeProvenance, type MotionProvenance } from '../../avatar';
import type { Dialect } from '../lib/types';

interface AvatarStageProps {
  sign: string;
  signLength: 'short' | 'medium' | 'long';
  nmf: string;
  chip: string;
  chipLive: boolean;
  index: number;
  total: number;
  counter: string;
  dialect: Dialect;
  playing: boolean;
  busy: boolean;
  cycleMs: number;
}

/**
 * The signing stage: a 3D avatar driven per gloss token, with the sign name and
 * non-manual feature read out beside it.
 *
 * The avatar only claims what it can source. Signs with no motion data rest
 * rather than inventing a form, and the frame says which of those it is showing
 * on every token — the gloss is a learning aid, and a wrong sign taught
 * confidently would be worse than an honest gap.
 */
export function AvatarStage({
  sign,
  signLength,
  nmf,
  chip,
  chipLive,
  index,
  total,
  counter,
  dialect,
  playing,
  busy,
  cycleMs,
}: AvatarStageProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const avatar = useRef<SigningAvatar | null>(null);
  const [rendering, setRendering] = useState(false);
  const [provenance, setProvenance] = useState<MotionProvenance | null>(null);

  useEffect(() => {
    if (!canvas.current) return;
    const instance = SigningAvatar.create({
      canvas: canvas.current,
      onProvenance: (kind) => setProvenance(kind),
    });
    avatar.current = instance;
    setRendering(Boolean(instance));

    return () => {
      instance?.dispose();
      avatar.current = null;
    };
  }, []);

  useEffect(() => {
    if (busy) avatar.current?.setThinking(true);
  }, [busy]);

  // The app owns the clock; the avatar is told which sign, how long it has, and
  // whether to animate it or hold it.
  useEffect(() => {
    if (busy) return;
    const next = avatar.current?.setToken(sign, cycleMs, playing);
    if (next) setProvenance(next);
  }, [sign, index, cycleMs, busy, playing]);

  useEffect(() => {
    avatar.current?.setPlaying(playing || busy);
  }, [playing, busy]);

  const description = total
    ? `Signing stage. Sign ${index + 1} of ${total}: ${sign}${
        nmf ? `, non-manual feature ${nmf}` : ''
      }`
    : 'Signing stage.';

  const motionLabel = provenance ? describeProvenance(provenance) : 'Avatar stage';
  const unverified = provenance ? provenance.kind !== 'reviewed' : true;

  return (
    <section className="stage" aria-label="Auslan signer">
      <div className="stage-head">
        <p className="kicker stage-label">Auslan signer · 3D avatar</p>
        <p className="stage-chip" data-live={chipLive} aria-live="polite">
          {chip}
        </p>
      </div>

      <div className="stage-frame" data-rendering={rendering} role="img" aria-label={description}>
        <span className="crop-mark" data-corner="tl" />
        <span className="crop-mark" data-corner="tr" />
        <span className="crop-mark" data-corner="bl" />
        <span className="crop-mark" data-corner="br" />

        <canvas className="stage-canvas" ref={canvas} aria-hidden="true" />

        <p className="stage-caption" data-unverified={unverified}>
          {motionLabel}
        </p>
        <p className="stage-counter">{counter}</p>
        <p className="stage-dialect">{dialect} Auslan</p>
      </div>

      <div className="stage-readout">
        <p className="stage-sign" data-length={signLength}>
          {sign}
        </p>
        <p className="stage-nmf">{nmf ? `NMF · ${nmf}` : ''}</p>
      </div>

      <div className="ticks" aria-hidden="true">
        {Array.from({ length: total }, (_, tick) => (
          <span key={tick} className="tick" data-filled={tick <= index} />
        ))}
      </div>
    </section>
  );
}
